import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropZone, TabNavigation } from "@beleef/ui";
import { FiAlertCircle, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import {
  AssetFileRow,
  AssetFolderTile,
} from "components/brandAssets/BrandAssetsBrowser";
import { useUploadFiles } from "apis/upload";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import SharingRow from "components/sharing/SharingRow";
import { showToast } from "components/ui/toast";
import {
  ASSET_STATUS,
  BRAND_ASSET_ACCEPT,
  BRAND_ASSET_CATEGORIES,
  BRAND_ASSET_LABELS,
  BRAND_ASSET_STATUS_OPTIONS,
  BRAND_ASSET_TAGS,
  COLOR_PATTERN,
  COLOR_PRESETS,
  LOGO_ACCEPT,
  GROUP_OVERRIDE_SETTINGS,
  CONTROL_LEVELS,
  DEFAULT_CONTROL_LEVEL,
  SHARED_RECORD_TYPES,
  SLUG_PATTERN,
  SMS_PROVIDER_OPTIONS,
  STATUS_SELECT_OPTIONS,
  brandAssetUploadFolder,
  buildBrandAssetState,
  buildLogoState,
  buildNetworkFormValues,
  buildNetworkPayload,
  getFieldError,
  getUploadedUrl,
  getVisibleLogoUrl,
  inputClassName,
  ASSISTANT_NAME_MAX_LENGTH,
  isDarkColor,
  isImageFile,
  logoUploadFolder,
  normalizeHexInput,
  optionalColor,
  optionalEmail,
  safeColor,
} from "utils/network";

// Each tab owns a slice of the form. `fields` are the top-level keys whose
// errors belong to that tab, so a validation failure in a tab you are not
// looking at still announces itself on the tab strip.
const TABS = [
  {
    value: "identity",
    label: "Identity",
    fields: ["name", "slug", "status", "trialEndsAt"],
  },
  { value: "assistant", label: "Assistant", fields: ["aiAssistant"] },
  { value: "branding", label: "Branding", fields: ["branding"] },
  { value: "assets", label: "Brand Assets", fields: [] },
  { value: "emails", label: "Emails", fields: ["operationalEmails"] },
  { value: "sms", label: "SMS", fields: ["integrations"] },
  { value: "overrides", label: "Overrides", fields: ["controlledBy"] },
  { value: "sharing", label: "Sharing", fields: ["sharing"] },
];

const Group = ({ label, children }) => (
  <div className="space-y-3">
    <div className="text-[11px] uppercase tracking-[0.06em] font-medium text-darkgray">
      {label}
    </div>
    {children}
  </div>
);

const Field = ({ label, error, hint, children }) => (
  <div className="space-y-2">
    <label className="form-label">{label}</label>
    {children}
    {hint ? <p className="text-xs text-darkgray">{hint}</p> : null}
    {error ? <p className="form-error-message">{error}</p> : null}
  </div>
);

const InputField = ({ label, error, hint, inputProps }) => (
  <Field label={label} error={error} hint={hint}>
    <input {...inputProps} className={inputClassName(error)} />
  </Field>
);

const LogoField = ({
  label,
  subtitle,
  displayUrl,
  onFilesSelected,
  onClear,
  disabled,
  error,
  // The ground this logo is meant for. A secondary logo is the reversed mark —
  // usually solid white — so previewing it on a light panel shows nothing at
  // all. Preview each one against the background it will actually be used on.
  previewBackground = "#FCFCFC",
}) => (
  <Field label={label} error={error} hint={displayUrl ? subtitle : undefined}>
    {displayUrl ? (
      <div
        className={`overflow-hidden rounded-md border ${
          error ? "border-red-500" : "border-mediumgray"
        }`}
      >
        {/* The logo sits inside its own margin rather than filling the frame —
            a wide wordmark otherwise runs edge to edge and reads as cramped. */}
        <div
          className="flex h-24 items-center justify-center px-8 py-5"
          style={{ backgroundColor: previewBackground }}
        >
          <img
            src={displayUrl}
            alt={label}
            className="max-h-9 max-w-[70%] object-contain"
          />
        </div>
        <div className="border-t border-mediumgray px-3 py-2 flex justify-end">
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            className="text-xs font-medium text-red-500 hover:underline disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      </div>
    ) : (
      <div
        className={error ? "rounded-md border border-red-500 p-px" : undefined}
      >
        <DropZone
          onFilesSelected={onFilesSelected}
          accept={LOGO_ACCEPT}
          disabled={disabled}
          title={`Upload ${label.toLowerCase()}`}
          subtitle="PNG, JPG, WEBP or SVG"
        />
      </div>
    )}
  </Field>
);


const ColorField = ({ label, name, error, hint, value, register, onColorChange }) => {
  const registration = register(name, { validate: optionalColor(label) });
  const hasValidColor = COLOR_PATTERN.test(value || "");

  return (
    <Field label={label} error={error} hint={hint}>
      <div className="space-y-2.5">
        <div className="flex items-center gap-2">
          <div
            className="h-[38px] w-[38px] rounded border border-mediumgray flex-shrink-0"
            style={{ backgroundColor: hasValidColor ? value : "#e5e7eb" }}
          />

          <div
            className={`flex flex-1 min-w-0 items-center rounded border bg-white px-2.5 ${
              error ? "border-red-500" : "border-mediumgray"
            }`}
          >
            <span className="text-sm text-darkgray">#</span>
            <input
              type="text"
              placeholder="000000"
              name={registration.name}
              ref={registration.ref}
              onBlur={registration.onBlur}
              value={(value || "").replace(/^#/, "")}
              onChange={(event) => onColorChange(name, event.target.value)}
              className="w-full min-w-0 border-0 bg-transparent px-1 py-2 text-sm text-black outline-none tabular-nums"
            />
          </div>

          <label className="relative flex h-[38px] cursor-pointer items-center rounded border border-mediumgray bg-lightergray px-3 text-sm font-medium text-darkergray hover:bg-lightgray flex-shrink-0">
            <input
              type="color"
              value={hasValidColor ? value : "#000000"}
              onChange={(event) => onColorChange(name, event.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
            Pick
          </label>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              aria-label={`Use ${preset}`}
              onClick={() => onColorChange(name, preset)}
              className="h-7 w-7 rounded border transition-transform hover:scale-105"
              style={{
                backgroundColor: preset,
                borderColor:
                  value === preset ? "#000000" : "rgba(0, 0, 0, 0.12)",
                borderWidth: value === preset ? 2 : 1,
              }}
            />
          ))}
        </div>
      </div>
    </Field>
  );
};

// Each panel shows only the logo meant for that background — no fallback to the
// other one, so a missing secondary reads as missing instead of looking fine.
const PreviewPanel = ({
  logoUrl,
  caption,
  background,
  textColor,
  placeholderClassName,
  placeholderStyle,
  className = "",
}) => (
  <div
    className={`flex flex-col items-center justify-center gap-3 px-5 py-8 ${className}`}
    style={{ backgroundColor: background, color: textColor }}
  >
    {logoUrl ? (
      <img
        src={logoUrl}
        alt={caption}
        className="max-h-11 max-w-[168px] object-contain"
      />
    ) : (
      <div
        className={`flex h-11 w-11 items-center justify-center rounded text-[10px] font-semibold ${placeholderClassName}`}
        style={placeholderStyle}
      >
        Logo
      </div>
    )}
    <p className="text-xs opacity-70">{caption}</p>
  </div>
);

const BrandPreview = ({ primaryLogoUrl, secondaryLogoUrl, colors }) => {
  const primaryColor = safeColor(colors.primary, "#000000");
  const secondaryColor = safeColor(colors.secondary, "#FFFFFF");
  const accentColor = safeColor(colors.accent, "#E5E7EB");

  return (
    <div className="overflow-hidden rounded-md border border-mediumgray">
      <PreviewPanel
        logoUrl={primaryLogoUrl}
        caption="Primary logo on light background"
        background="#FFFFFF"
        textColor="#818791"
        placeholderClassName="bg-lightgray text-darkgray"
      />
      <PreviewPanel
        logoUrl={secondaryLogoUrl}
        caption="Secondary logo on dark background"
        background={primaryColor}
        textColor={isDarkColor(primaryColor) ? "#FFFFFF" : "#000000"}
        placeholderStyle={{ backgroundColor: "rgba(255, 255, 255, 0.16)" }}
        className="border-t border-mediumgray"
      />
      <div className="flex flex-wrap gap-6 border-t border-mediumgray bg-lightergray px-5 py-3.5">
        {[
          { label: "Primary", value: primaryColor },
          { label: "Secondary", value: secondaryColor },
          { label: "Accent", value: accentColor },
        ].map((color) => (
          <div key={color.label} className="flex items-center gap-2.5">
            <div
              className="h-[22px] w-[22px] rounded border border-black/10"
              style={{ backgroundColor: color.value }}
            />
            <div className="leading-tight">
              <div className="text-[11px] text-darkgray">{color.label}</div>
              <div className="text-xs font-medium text-black tabular-nums">
                {color.value}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Both logos are required. Branding is rendered on light and dark grounds and
// neither falls back to the other, so a network with one logo is unusable on
// half its output. The files live in component state rather than the form, so
// this is checked by hand instead of through react-hook-form.
const findMissingLogos = (logos) => {
  const missing = {};
  if (!getVisibleLogoUrl(logos.primary)) {
    missing.primary = "Primary logo is required";
  }
  if (!getVisibleLogoUrl(logos.secondary)) {
    missing.secondary = "Secondary logo is required";
  }
  return missing;
};

export default function NetworkEdit({ initialData = {}, onSubmit }) {
  const [activeTab, setActiveTab] = useState(TABS[0].value);
  const [logoErrors, setLogoErrors] = useState({});
  const [logos, setLogos] = useState({
    primary: buildLogoState(initialData.branding?.logos?.primary),
    secondary: buildLogoState(initialData.branding?.logos?.secondary),
  });
  const [assets, setAssets] = useState(() =>
    buildBrandAssetState(initialData.brandAssets),
  );
  // Files picked but not yet uploaded, keyed by category. Held back so an
  // abandoned form leaves nothing behind in S3.
  const [pendingAssets, setPendingAssets] = useState({});
  // null shows the folder grid; a category shows what is inside it.
  const [openCategory, setOpenCategory] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: buildNetworkFormValues(initialData),
  });
  const { uploadSingle } = useUploadFiles();

  useEffect(
    () => () => {
      if (logos.primary.previewUrl)
        URL.revokeObjectURL(logos.primary.previewUrl);
    },
    [logos.primary.previewUrl],
  );

  useEffect(
    () => () => {
      if (logos.secondary.previewUrl) {
        URL.revokeObjectURL(logos.secondary.previewUrl);
      }
    },
    [logos.secondary.previewUrl],
  );

  const handleLogoSelected = (type) => (files) => {
    const file = files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      showToast("error", "Please upload a valid image file for the logo");
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setLogos((current) => ({
      ...current,
      [type]: {
        file,
        previewUrl,
        uploadedUrl: current[type].uploadedUrl,
        removed: false,
      },
    }));
    setLogoErrors((current) => ({ ...current, [type]: undefined }));
  };

  // Removing a required logo makes the form invalid straight away, so say so
  // now rather than waiting for a failed submit.
  const handleClearLogo = (type) => () => {
    setLogos((current) => ({
      ...current,
      [type]: { file: null, previewUrl: "", uploadedUrl: "", removed: true },
    }));
    setLogoErrors((current) => ({
      ...current,
      [type]: `${type === "primary" ? "Primary" : "Secondary"} logo is required`,
    }));
  };

  const handleAssetFilesSelected = (category) => (files) => {
    const picked = Array.from(files || []).filter(Boolean);
    if (!picked.length) return;

    setPendingAssets((current) => ({
      ...current,
      [category]: [
        ...(current[category] || []),
        ...picked.map((file) => ({ file, tag: "" })),
      ],
    }));
  };

  const updatePendingAsset = (category, index, changes) =>
    setPendingAssets((current) => ({
      ...current,
      [category]: (current[category] || []).map((item, position) =>
        position === index ? { ...item, ...changes } : item,
      ),
    }));

  const removePendingAsset = (category, index) =>
    setPendingAssets((current) => ({
      ...current,
      [category]: (current[category] || []).filter(
        (_, position) => position !== index,
      ),
    }));

  const updateAssetCategory = (category, changes) =>
    setAssets((current) => ({
      ...current,
      [category]: {
        status: "pending",
        submittedAt: "",
        files: [],
        ...current[category],
        ...changes,
      },
    }));

  // Drops the reference, not the object in S3 — a file may already be with a
  // designer or on a placed order.
  const removeSavedAssetFile = (category, index) =>
    updateAssetCategory(category, {
      files: (assets[category]?.files || []).filter(
        (_, position) => position !== index,
      ),
    });

  const updateSavedAssetFile = (category, index, changes) =>
    updateAssetCategory(category, {
      files: (assets[category]?.files || []).map((file, position) =>
        position === index ? { ...file, ...changes } : file,
      ),
    });

  const assetFileCount = (category) =>
    (assets[category]?.files?.length || 0) +
    (pendingAssets[category]?.length || 0);

  // Uploads everything picked this session, then returns the complete list —
  // the backend replaces the whole array, so every category has to be here.
  const resolveBrandAssets = async (slug) => {
    const entries = [];

    for (const category of BRAND_ASSET_CATEGORIES) {
      const saved = assets[category];
      const picked = pendingAssets[category] || [];

      const uploaded = await Promise.all(
        picked.map(async (item) => {
          const result = await uploadSingle({
            file: item.file,
            folder: brandAssetUploadFolder(initialData, slug, category),
            raw: true,
          });
          const fileUrl = getUploadedUrl(result);

          if (!fileUrl) {
            throw new Error(`${BRAND_ASSET_LABELS[category]} upload failed`);
          }
          return { fileUrl, originalName: item.file.name, tag: item.tag || "" };
        }),
      );

      const files = [...(saved?.files || []), ...uploaded];
      if (!files.length) continue;

      entries.push({
        category,
        files,
        status: saved?.status || "pending",
        submittedAt: saved?.submittedAt || new Date().toISOString(),
      });
    }

    return entries;
  };

  const handleColorChange = (fieldName, rawValue) => {
    setValue(fieldName, normalizeHexInput(rawValue), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const resolveLogoUrl = async (logo, folder, label) => {
    if (logo.file) {
      const uploadResult = await uploadSingle({ file: logo.file, folder });
      const uploadedUrl = getUploadedUrl(uploadResult);

      if (!uploadedUrl) throw new Error(`${label} upload failed`);
      return uploadedUrl;
    }

    if (logo.removed) return "";
    return logo.uploadedUrl || "";
  };

  const submitForm = async (data) => {
    // Checked before uploading, so a half-filled form does not push a file to
    // S3 and then refuse to save.
    const missingLogos = findMissingLogos(logos);
    if (Object.keys(missingLogos).length > 0) {
      setLogoErrors(missingLogos);
      setActiveTab("branding");
      return;
    }
    setLogoErrors({});

    try {
      const folder = logoUploadFolder(initialData, data.slug);
      const [primaryLogo, secondaryLogo] = await Promise.all([
        resolveLogoUrl(logos.primary, folder, "Primary logo"),
        resolveLogoUrl(logos.secondary, folder, "Secondary logo"),
      ]);
      const brandAssets = await resolveBrandAssets(data.slug);

      // Uploaded files become saved ones, so a save that fails after the upload
      // can be retried without pushing the same artwork to S3 twice.
      setAssets(buildBrandAssetState(brandAssets));
      setPendingAssets({});

      // Compliance, templates and toneOfVoice are not edited here. The backend
      // only writes the keys it receives, so leaving them out keeps whatever is
      // stored rather than echoing back a stale copy.
      onSubmit(
        buildNetworkPayload(data, { primaryLogo, secondaryLogo, brandAssets }),
      );
    } catch (error) {
      showToast("error", error?.message || "Upload failed");
    }
  };

  const primaryColor = watch("branding.colors.primary") || "";
  const secondaryColor = watch("branding.colors.secondary") || "";
  const accentColor = watch("branding.colors.accent") || "";
  const headerColor = watch("branding.colors.header") || "";
  const primaryLogoUrl = getVisibleLogoUrl(logos.primary);
  const secondaryLogoUrl = getVisibleLogoUrl(logos.secondary);
  const isTwilioProvider = watch("integrations.sms.smsProvider") === "twilio";

  // Show the secondary logo on the colour it will actually sit on. If the brand
  // colour is itself light, fall back to a neutral dark — otherwise a white
  // reversed logo would be invisible here too.
  const brandGround = safeColor(primaryColor, "#000000");
  const secondaryPreviewGround = isDarkColor(brandGround)
    ? brandGround
    : "#1F2A37";

  // Fields in an inactive tab are unmounted but stay registered, so their
  // errors still exist on submit — flag the tab so they are not invisible.
  const hasLogoError = Object.values(logoErrors).some(Boolean);
  const tabs = useMemo(
    () =>
      TABS.map((tab) => {
        const failed =
          tab.fields.some((field) => errors[field]) ||
          (tab.value === "branding" && hasLogoError);

        return {
          value: tab.value,
          label: tab.label,
          icon: failed ? FiAlertCircle : undefined,
        };
      }),
    [errors, hasLogoError],
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Outside the form on purpose: @beleef/ui TabNavigation renders its tabs
          as <button> with no type, which defaults to submit inside a form — so
          switching tabs would submit and close the modal. */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <form onSubmit={handleSubmit(submitForm)} className="flex flex-col gap-5">
        {activeTab === "identity" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField
              label="Name *"
              error={getFieldError(errors, "name")}
              inputProps={{
                type: "text",
                placeholder: "Stone Real Estate",
                ...register("name", { required: "Name is required" }),
              }}
            />
            <InputField
              label="Slug *"
              error={getFieldError(errors, "slug")}
              hint="This network's subdomain, and the key its marketing templates are registered under."
              inputProps={{
                type: "text",
                placeholder: "stone",
                ...register("slug", {
                  required: "Slug is required",
                  pattern: {
                    value: SLUG_PATTERN,
                    message:
                      "Slug must use lowercase letters, numbers and hyphens only",
                  },
                }),
              }}
            />
            <Field label="Status" error={getFieldError(errors, "status")}>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <SearchSelect
                    options={STATUS_SELECT_OPTIONS}
                    value={
                      STATUS_SELECT_OPTIONS.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="Select status..."
                    searchable={false}
                  />
                )}
              />
            </Field>
            <InputField
              label="Trial ends"
              error={getFieldError(errors, "trialEndsAt")}
              hint="Until the end of this day, agents in this network are not shown the campaign paywall and no campaign is counted against a plan. Leave blank for no trial."
              inputProps={{
                type: "date",
                ...register("trialEndsAt"),
              }}
            />
          </div>
        ) : null}

        {activeTab === "assistant" ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-darkgray">
              This is the default assistant identity for the network. A group
              or an office can override the name for its own agents; blank
              lower-level fields inherit this value. The sending address follows
              the name on its own: sandy@beleef.com.au for Sandy,
              notifications@beleef.com.au for any other name.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Assistant name"
                hint="Set the agency's own name. Blank falls back to Sandy."
                error={getFieldError(errors, "aiAssistant.name")}
                inputProps={{
                  type: "text",
                  maxLength: ASSISTANT_NAME_MAX_LENGTH,
                  ...register("aiAssistant.name", {
                    maxLength: {
                      value: ASSISTANT_NAME_MAX_LENGTH,
                      message: `Assistant name must be ${ASSISTANT_NAME_MAX_LENGTH} characters or fewer`,
                    },
                  }),
                }}
              />
              <InputField
                label="Assistant photo URL"
                hint="Blank uses the default face."
                error={getFieldError(errors, "aiAssistant.picture")}
                inputProps={{
                  type: "url",
                  ...register("aiAssistant.picture"),
                }}
              />
            </div>
          </div>
        ) : null}

        {activeTab === "branding" ? (
          <div className="flex flex-col gap-5">
            <p className="text-xs text-darkgray">
              Applies to every organization in this network. The ad copy
              disclaimer stays at organization level.
            </p>

            <Group label="Logos">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <LogoField
                  label="Primary Logo *"
                  subtitle="Used on light backgrounds like documents and proposals."
                  displayUrl={primaryLogoUrl}
                  onFilesSelected={handleLogoSelected("primary")}
                  onClear={handleClearLogo("primary")}
                  disabled={isSubmitting}
                  error={logoErrors.primary}
                />
                <LogoField
                  label="Secondary Logo *"
                  subtitle="Used on dark backgrounds like headers and signboards."
                  displayUrl={secondaryLogoUrl}
                  onFilesSelected={handleLogoSelected("secondary")}
                  onClear={handleClearLogo("secondary")}
                  disabled={isSubmitting}
                  error={logoErrors.secondary}
                  previewBackground={secondaryPreviewGround}
                />
              </div>
            </Group>

            <Group label="Palette">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <ColorField
                  label="Primary"
                  name="branding.colors.primary"
                  error={getFieldError(errors, "branding.colors.primary")}
                  value={primaryColor}
                  register={register}
                  onColorChange={handleColorChange}
                />
                <ColorField
                  label="Secondary"
                  name="branding.colors.secondary"
                  error={getFieldError(errors, "branding.colors.secondary")}
                  value={secondaryColor}
                  register={register}
                  onColorChange={handleColorChange}
                />
                <ColorField
                  label="Accent"
                  name="branding.colors.accent"
                  error={getFieldError(errors, "branding.colors.accent")}
                  value={accentColor}
                  register={register}
                  onColorChange={handleColorChange}
                />
                <ColorField
                  label="Link Header"
                  name="branding.colors.header"
                  error={getFieldError(errors, "branding.colors.header")}
                  hint="Behind the logo at the top of shared links. Blank uses Primary."
                  value={headerColor}
                  register={register}
                  onColorChange={handleColorChange}
                />
              </div>
            </Group>

            <Group label="Preview">
              <BrandPreview
                primaryLogoUrl={primaryLogoUrl}
                secondaryLogoUrl={secondaryLogoUrl}
                colors={{
                  primary: primaryColor,
                  secondary: secondaryColor,
                  accent: accentColor,
                }}
              />
            </Group>

            <Group label="Typography">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Heading Font"
                  error={getFieldError(errors, "branding.fonts.heading")}
                  inputProps={{
                    type: "text",
                    placeholder: "Helvetica",
                    ...register("branding.fonts.heading"),
                  }}
                />
                <InputField
                  label="Body Font"
                  error={getFieldError(errors, "branding.fonts.body")}
                  inputProps={{
                    type: "text",
                    placeholder: "Helvetica",
                    ...register("branding.fonts.body"),
                  }}
                />
              </div>
            </Group>

            <Group label="Links">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Privacy Policy URL"
                  error={getFieldError(errors, "branding.privacyPolicyUrl")}
                  inputProps={{
                    type: "url",
                    placeholder: "https://...",
                    ...register("branding.privacyPolicyUrl"),
                  }}
                />
                <InputField
                  label="Facebook"
                  error={getFieldError(errors, "branding.socialLinks.facebook")}
                  inputProps={{
                    type: "url",
                    placeholder: "https://facebook.com/...",
                    ...register("branding.socialLinks.facebook"),
                  }}
                />
                <InputField
                  label="Instagram"
                  error={getFieldError(
                    errors,
                    "branding.socialLinks.instagram",
                  )}
                  inputProps={{
                    type: "url",
                    placeholder: "https://instagram.com/...",
                    ...register("branding.socialLinks.instagram"),
                  }}
                />
                <InputField
                  label="LinkedIn"
                  error={getFieldError(errors, "branding.socialLinks.linkedin")}
                  inputProps={{
                    type: "url",
                    placeholder: "https://linkedin.com/...",
                    ...register("branding.socialLinks.linkedin"),
                  }}
                />
                <InputField
                  label="YouTube"
                  error={getFieldError(errors, "branding.socialLinks.youtube")}
                  inputProps={{
                    type: "url",
                    placeholder: "https://youtube.com/...",
                    ...register("branding.socialLinks.youtube"),
                  }}
                />
              </div>
            </Group>
          </div>
        ) : null}

        {activeTab === "assets" ? (
          <div className="flex flex-col gap-4">
            {openCategory ? (
              <>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setOpenCategory(null)}
                    className="flex items-center gap-1 rounded px-1.5 py-1 text-[13px] text-darkgray hover:bg-lightgray hover:text-black"
                  >
                    <FiChevronLeft className="h-4 w-4" />
                    Brand assets
                  </button>
                  <FiChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-darkgray" />
                  <span className="truncate text-[13px] font-medium text-black">
                    {BRAND_ASSET_LABELS[openCategory]}
                  </span>
                </div>

                <div className="sm:max-w-xs">
                  <Field
                    label="Design status"
                    hint="Where this category is up to. Adding artwork does not move it."
                  >
                    <SearchSelect
                      options={BRAND_ASSET_STATUS_OPTIONS}
                      value={
                        BRAND_ASSET_STATUS_OPTIONS.find(
                          (option) =>
                            option.value ===
                            (assets[openCategory]?.status || "pending"),
                        ) || null
                      }
                      onChange={(option) =>
                        updateAssetCategory(openCategory, {
                          status: option?.value || "pending",
                        })
                      }
                      placeholder="Select status..."
                      searchable={false}
                      disabled={isSubmitting}
                    />
                  </Field>
                </div>

                {assetFileCount(openCategory) > 0 ? (
                  // No overflow-hidden: it would clip the tag dropdown, which
                  // opens past the bottom row however high its z-index is.
                  <div className="rounded-md border border-mediumgray">
                    {(assets[openCategory]?.files || []).map((file, index) => (
                      <AssetFileRow
                        key={file.fileUrl}
                        name={file.originalName || file.fileUrl.split("/").pop()}
                        href={file.fileUrl}
                        tag={file.tag}
                        tags={BRAND_ASSET_TAGS[openCategory] || []}
                        onTagChange={(tag) =>
                          updateSavedAssetFile(openCategory, index, { tag })
                        }
                        onRemove={() =>
                          removeSavedAssetFile(openCategory, index)
                        }
                        disabled={isSubmitting}
                      />
                    ))}
                    {(pendingAssets[openCategory] || []).map((item, index) => (
                      <AssetFileRow
                        key={`${item.file.name}-${index}`}
                        name={item.file.name}
                        tag={item.tag}
                        tags={BRAND_ASSET_TAGS[openCategory] || []}
                        onTagChange={(tag) =>
                          updatePendingAsset(openCategory, index, { tag })
                        }
                        onRemove={() => removePendingAsset(openCategory, index)}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                ) : null}

                <DropZone
                  onFilesSelected={handleAssetFilesSelected(openCategory)}
                  accept={BRAND_ASSET_ACCEPT}
                  multiple
                  disabled={isSubmitting}
                  title={`Upload ${BRAND_ASSET_LABELS[openCategory].toLowerCase()} artwork`}
                  subtitle="PDF, AI, EPS, PNG or JPG — up to 50MB each"
                />
              </>
            ) : (
              <>
                <p className="text-xs text-darkgray">
                  Raw artwork for the whole network, stored exactly as uploaded.
                  Open a folder to add files. Every organization inherits these
                  unless Brand assets is switched on under Overrides, in which
                  case the group's own list replaces this one outright.
                </p>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                  {BRAND_ASSET_CATEGORIES.map((category) => (
                    <AssetFolderTile
                      key={category}
                      label={BRAND_ASSET_LABELS[category]}
                      count={assetFileCount(category)}
                      status={
                        ASSET_STATUS[assets[category]?.status] ||
                        ASSET_STATUS.pending
                      }
                      onOpen={() => setOpenCategory(category)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}

        {activeTab === "emails" ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-darkgray">
              Where this network's operational mail goes. Organizations can
              point these at their own inboxes when the Emails override is on.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Settlements"
                error={getFieldError(errors, "operationalEmails.settlements")}
                inputProps={{
                  type: "email",
                  placeholder: "settlements@example.com.au",
                  ...register("operationalEmails.settlements", {
                    validate: optionalEmail("Settlements email"),
                  }),
                }}
              />
              <InputField
                label="Concierge"
                error={getFieldError(errors, "operationalEmails.concierge")}
                inputProps={{
                  type: "email",
                  placeholder: "concierge@example.com.au",
                  ...register("operationalEmails.concierge", {
                    validate: optionalEmail("Concierge email"),
                  }),
                }}
              />
              <InputField
                label="Key Events"
                error={getFieldError(errors, "operationalEmails.keyEvents")}
                inputProps={{
                  type: "email",
                  placeholder: "events@example.com.au",
                  ...register("operationalEmails.keyEvents", {
                    validate: optionalEmail("Key events email"),
                  }),
                }}
              />
              <InputField
                label="Accounts"
                error={getFieldError(errors, "operationalEmails.accounts")}
                inputProps={{
                  type: "email",
                  placeholder: "accounts@example.com.au",
                  ...register("operationalEmails.accounts", {
                    validate: optionalEmail("Accounts email"),
                  }),
                }}
              />
              <InputField
                label="Admin"
                error={getFieldError(errors, "operationalEmails.admin")}
                inputProps={{
                  type: "email",
                  placeholder: "admin@example.com.au",
                  ...register("operationalEmails.admin", {
                    validate: optionalEmail("Admin email"),
                  }),
                }}
              />
            </div>
          </div>
        ) : null}

        {activeTab === "sms" ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-darkgray">
              Only the selected provider's credentials are saved.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field
                label="Provider"
                error={getFieldError(errors, "integrations.sms.smsProvider")}
              >
                <Controller
                  control={control}
                  name="integrations.sms.smsProvider"
                  render={({ field }) => (
                    <SearchSelect
                      options={SMS_PROVIDER_OPTIONS}
                      value={
                        SMS_PROVIDER_OPTIONS.find(
                          (option) => option.value === field.value,
                        ) || null
                      }
                      onChange={(option) => field.onChange(option?.value || "")}
                      placeholder="Select provider..."
                      searchable={false}
                    />
                  )}
                />
              </Field>

              {isTwilioProvider ? (
                <>
                  <InputField
                    label="Account SID"
                    error={getFieldError(
                      errors,
                      "integrations.sms.twilioAccountSid",
                    )}
                    inputProps={{
                      type: "text",
                      placeholder: "AC...",
                      ...register("integrations.sms.twilioAccountSid"),
                    }}
                  />
                  <InputField
                    label="Auth Token"
                    error={getFieldError(
                      errors,
                      "integrations.sms.twilioAuthToken",
                    )}
                    inputProps={{
                      type: "text",
                      ...register("integrations.sms.twilioAuthToken"),
                    }}
                  />
                </>
              ) : (
                <>
                  <InputField
                    label="Username"
                    error={getFieldError(errors, "integrations.sms.username")}
                    inputProps={{
                      type: "text",
                      ...register("integrations.sms.username"),
                    }}
                  />
                  <InputField
                    label="Password"
                    error={getFieldError(errors, "integrations.sms.password")}
                    inputProps={{
                      type: "text",
                      ...register("integrations.sms.password"),
                    }}
                  />
                </>
              )}

              <InputField
                label="Alpha Sender ID"
                hint="The name recipients see instead of a number."
                error={getFieldError(errors, "integrations.sms.alphaSenderId")}
                inputProps={{
                  type: "text",
                  placeholder: "Stone",
                  ...register("integrations.sms.alphaSenderId"),
                }}
              />
            </div>
          </div>
        ) : null}

        {activeTab === "overrides" ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-darkgray">
              How deep each setting may be changed. Network only means this
              network's value applies everywhere. A division may narrow its own
              setting further, but can never open one up beyond what is set here.
            </p>
            <div className="border border-mediumgray rounded-md overflow-hidden divide-y divide-mediumgray">
              {GROUP_OVERRIDE_SETTINGS.map((setting) => (
                <Controller
                  key={setting.key}
                  control={control}
                  name={`controlledBy.${setting.key}`}
                  render={({ field }) => (
                    <div className="flex items-center justify-between gap-4 p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{setting.name}</p>
                        <p className="text-xs text-darkgray">
                          {setting.description}
                        </p>
                      </div>
                      <select
                        className="border border-mediumgray rounded-md px-2 py-1.5 text-sm shrink-0"
                        value={field.value || DEFAULT_CONTROL_LEVEL}
                        onChange={(e) => field.onChange(e.target.value)}
                        disabled={isSubmitting}
                      >
                        {CONTROL_LEVELS.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                />
              ))}
            </div>
          </div>
        ) : null}

        {activeTab === "sharing" ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-darkgray">
              What this network pools across its divisions and groups. Sharing is
              mutual — a level that pools both contributes and sees. A division
              or group can set its own, and the most specific decision wins, so
              a division may close what the network opened. Whether they are
              allowed to is set on the Overrides tab, under Sharing.
            </p>
            <div className="border border-mediumgray rounded-md overflow-hidden">
              {SHARED_RECORD_TYPES.map((recordType) => (
                <Controller
                  key={recordType.key}
                  control={control}
                  name={`sharing.${recordType.key}`}
                  render={({ field }) => (
                    <SharingRow
                      recordType={recordType}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  )}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-mediumgray pt-4">
          {Object.keys(errors).length > 0 || hasLogoError ? (
            <span className="text-xs text-red-500 mr-auto">
              Some fields need attention — check the flagged tabs.
            </span>
          ) : null}
          <Button
            type="submit"
            className="black-button"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {initialData._id ? "Update Network" : "Create Network"}
          </Button>
        </div>
      </form>
    </div>
  );
}
